import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { CTAButton } from "./CTAButton";
import { Countdown } from "./Countdown";

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1], delay: 0.15 + i * 0.12 },
  }),
};

const Line = ({ children, i, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      custom={i}
      initial="hidden"
      animate="visible"
      variants={lineVariants}
      className={className}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "35%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <section
      ref={ref}
      style={{ paddingTop: "calc(120px + env(safe-area-inset-top, 0px))" }}
      className="relative flex min-h-[100svh] flex-col items-center justify-center overflow-hidden px-4 pb-10 text-center sm:px-8"
    >
      <motion.div
        style={{ y: bgY, scale: bgScale }}
        className="absolute inset-0 -z-10"
      >
        <div
          aria-hidden="true"
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 50% at 78% 28%, rgba(15,147,204,0.35) 0%, rgba(15,147,204,0.12) 35%, rgba(8,9,11,0) 60%), #08090b",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#08090b]/20 via-[#08090b]/60 to-[#08090b]" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1, duration: 0.6 }}
        className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#0f93cc]/40 bg-[#0f93cc]/10 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.25em] text-[#5bc4ee] sm:text-xs"
        data-testid="hero-eyebrow"
      >
        Teacher&rsquo;s Day Reveal
      </motion.div>

      <h1 className="font-display max-w-4xl text-[2.1rem] leading-[1.08] font-extrabold text-white sm:text-5xl sm:leading-[1.05] lg:text-6xl">
        <Line i={0}>Something BIG is</Line>
        <Line i={1} className="text-[#0f93cc]">
          Coming to the MIK Ecosystem 🔥
        </Line>
      </h1>

      <motion.p
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.75, duration: 0.7 }}
        className="mx-auto mt-5 max-w-xl text-[15px] text-white/65 sm:mt-6 sm:text-lg"
      >
        Around Teacher&rsquo;s Day, MIK is launching its biggest-ever offers across the
        full MIK ecosystem — courses, books &amp; more — and some of it will be limited-access.
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.95, duration: 0.7 }}
        className="mt-9 w-full max-w-sm sm:max-w-none"
      >
        <CTAButton label="Click Here to Join WhatsApp Community" testId="hero-cta-whatsapp" size="large" />
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.15, duration: 0.7 }}
        className="mt-10"
      >
        <Countdown testId="hero-countdown" />
      </motion.div>
    </section>
  );
};
